<template>
  <div id="app">
    <nprogress-container />
    <transition
      name="fade"
      mode="out-in"
    >
      <router-view />
    </transition>
  </div>
</template>

<script>
import NprogressContainer from 'vue-nprogress/src/NprogressContainer';

const MOBILE_PULL_REFRESH_MAX_SHORT_SIDE = 1024;
const MOBILE_UA_RE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;

export default {
  name: 'app',
  components: {
    NprogressContainer,
  },
  mounted() {
    this.refreshPullToRefreshGuard();
    window.addEventListener('resize', this.refreshPullToRefreshGuard);
    window.addEventListener('orientationchange', this.refreshPullToRefreshGuard);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.refreshPullToRefreshGuard);
    window.removeEventListener('orientationchange', this.refreshPullToRefreshGuard);
    this.unbindPullToRefreshGuard();
  },
  methods: {
    shouldDisableMobilePullToRefresh() {
      if (typeof window === 'undefined') return false;

      const shortSide = Math.min(window.screen.width || 0, window.screen.height || 0);
      const isTouchDevice = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
      const hasCoarsePointer = window.matchMedia
        ? window.matchMedia('(hover: none) and (pointer: coarse)').matches
        : false;
      const ua = navigator.userAgent || '';

      return isTouchDevice && (
        hasCoarsePointer ||
        MOBILE_UA_RE.test(ua) ||
        (shortSide > 0 && shortSide <= MOBILE_PULL_REFRESH_MAX_SHORT_SIDE)
      );
    },
    isScrollableElement(node) {
      if (!(node instanceof HTMLElement)) return false;

      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;

      return /(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight;
    },
    getScrollableParents(target) {
      const parents = [];
      let node = target instanceof HTMLElement ? target : target && target.parentElement;

      while (node && node !== document.body && node !== document.documentElement) {
        if (this.isScrollableElement(node)) {
          parents.push(node);
        }
        node = node.parentElement;
      }

      return parents;
    },
    refreshPullToRefreshGuard() {
      if (this.shouldDisableMobilePullToRefresh()) {
        this.bindPullToRefreshGuard();
      } else {
        this.unbindPullToRefreshGuard();
      }
    },
    bindPullToRefreshGuard() {
      if (this.pullToRefreshGuardBound || typeof document === 'undefined') return;

      this.pullToRefreshTouchStartHandler = (event) => {
        if (!event.touches || event.touches.length !== 1) return;

        const touch = event.touches[0];
        this.pullToRefreshStartX = touch.clientX;
        this.pullToRefreshStartY = touch.clientY;
        this.pullToRefreshScrollParents = this.getScrollableParents(event.target);
      };

      this.pullToRefreshTouchMoveHandler = (event) => {
        if (!this.shouldDisableMobilePullToRefresh()) return;
        if (!event.touches || event.touches.length !== 1) return;

        const touch = event.touches[0];
        const deltaX = touch.clientX - (this.pullToRefreshStartX || 0);
        const deltaY = touch.clientY - (this.pullToRefreshStartY || 0);

        if (deltaY <= 0 || Math.abs(deltaY) <= Math.abs(deltaX)) return;

        const scrollParents = this.pullToRefreshScrollParents || this.getScrollableParents(event.target);

        if (!scrollParents.length || scrollParents.every(node => node.scrollTop <= 0)) {
          if (event.cancelable) {
            event.preventDefault();
          }
        }
      };

      this.pullToRefreshTouchEndHandler = () => {
        this.pullToRefreshStartX = 0;
        this.pullToRefreshStartY = 0;
        this.pullToRefreshScrollParents = null;
      };

      document.addEventListener('touchstart', this.pullToRefreshTouchStartHandler, { passive: true });
      document.addEventListener('touchmove', this.pullToRefreshTouchMoveHandler, { passive: false });
      document.addEventListener('touchend', this.pullToRefreshTouchEndHandler, { passive: true });
      document.addEventListener('touchcancel', this.pullToRefreshTouchEndHandler, { passive: true });

      this.pullToRefreshGuardBound = true;
    },
    unbindPullToRefreshGuard() {
      if (!this.pullToRefreshGuardBound || typeof document === 'undefined') return;

      document.removeEventListener('touchstart', this.pullToRefreshTouchStartHandler);
      document.removeEventListener('touchmove', this.pullToRefreshTouchMoveHandler);
      document.removeEventListener('touchend', this.pullToRefreshTouchEndHandler);
      document.removeEventListener('touchcancel', this.pullToRefreshTouchEndHandler);

      this.pullToRefreshTouchStartHandler = null;
      this.pullToRefreshTouchMoveHandler = null;
      this.pullToRefreshTouchEndHandler = null;
      this.pullToRefreshScrollParents = null;
      this.pullToRefreshGuardBound = false;
    },
  },
};
</script>

<style lang="less">
@import './assets/css/myskin.less';

.clearfix:after {
  visibility: hidden;
  display: block;
  font-size: 0;
  content: ' ';
  clear: both;
  height: 0;
}

body {
  margin: 0px;
  padding: 0px;
  overscroll-behavior-y: none;
  font-family: 'DINPro-Medium', -apple-system, BlinkMacSystemFont, Segoe UI,
    PingFang SC, Hiragino Sans GB, Microsoft YaHei, Helvetica Neue, Helvetica,
    Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
  -webkit-font-smoothing: antialiased;

  -webkit-font-smoothing: antialiased;
}

#app {
  position: absolute;
  top: 0px;
  bottom: 0px;
  width: 100%;
  overscroll-behavior-y: none;
}

html {
  overscroll-behavior-y: none;
}

a {
  cursor: pointer;
}

// overrides el-message-box__btns:
// define message-box btns direction
.el-message-box__btns {
  direction: rtl;
}
.el-message-box__btns button:nth-child(1) {
  margin-left: 10px;
}
.el-message-box__btns button:nth-child(2) {
  margin-left: 0;
}

.el-submenu [class^='fa'] {
  vertical-align: baseline;
  margin-right: 10px;
}

.el-menu-item [class^='fa'] {
  vertical-align: baseline;
  margin-right: 10px;
}

.toolbar {
  background: #f2f2f2;
  padding: 10px;
  //border:1px solid #dfe6ec;
  margin: 10px 0px;
  .el-form-item {
    margin-bottom: 10px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
input::-webkit-input-placeholder {
  /* 强行设置placeholder颜色 */
  color: #a9abb0 !important;
}
.el-pagination {
  font-weight: 500;
}
// 强行设置预览弹窗的z-index为20000，防止饿了么弹窗高于这个z-index
.viewer-container {
  z-index: 20000 !important;
}

@keyframes active-twinkling {
  0% {
    background: #fff;
  }
  100% {
    background: #f7f0c7;
  }
}

@-webkit-keyframes active-twinkling {
  0% {
    background: #fff;
  }
  100% {
    background: #f7f0c7;
  }
}
</style>
